import { slugify } from '@lib/utilities/text/slugify'
import type { FacetType } from '../const'
import { FACET_ORDER, FACET_PREFIX_MAP, LOCATION_FACETS } from '../const'

/**
 * Constructs a slug in the proper order from facet values
 * @param facets An object containing facet values to include in the slug
 * @returns A new slug with facets in the proper order
 */
export default function createSlugFromFacets(
  facets: Partial<Record<FacetType, string>>,
): string {
  // Create a processed copy with slugified values
  const processedFacets: Partial<Record<FacetType, string>> = {}
  for (const [key, value] of Object.entries(facets) as [FacetType, string][]) {
    // Skip the 'paid' facet here, we'll handle it separately
    if (key === 'paid') continue

    if (value) {
      processedFacets[key] = slugify(value)
    }
  }

  // Handle location facets (only one should be present)
  // Using the priority order: city, state, hospital
  const presentLocationFacets = LOCATION_FACETS.filter(
    (facet) => facet in processedFacets,
  )

  if (presentLocationFacets.length > 1) {
    // Keep only the highest priority location facet
    const keepFacet = presentLocationFacets[0]
    LOCATION_FACETS.forEach((facet) => {
      if (facet !== keepFacet) {
        delete processedFacets[facet]
      }
    })
  }

  // Generate segments for facets with values
  const segments = FACET_ORDER.filter(
    (facet) => facet !== 'paid' && processedFacets[facet],
  ).map((facet) => `${FACET_PREFIX_MAP[facet]}${processedFacets[facet]}`)

  // Add paid facet at the end if it exists in the input
  if ('paid' in facets) {
    segments.push(FACET_PREFIX_MAP.paid)
  }

  // Join segments with hyphens
  return segments.join('-')
}
